import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Warning,
  Shield,
  CurrencyDollar,
  ChartLineUp,
  Gauge,
  Bank,
  CreditCard,
  Coins,
  ArrowsLeftRight,
  Printer,
} from "@phosphor-icons/react";

const LOGO_URL = "/logo.png";

// ------- Primitives -------
const SectionLabel = ({ num, children }) => (
  <div className="flex items-baseline gap-3 mb-6 pb-3 border-b border-slate-200">
    <span className="font-mark text-[11px] uppercase tracking-[0.28em] text-[#1E3A6F] tabular-nums">
      {num}
    </span>
    <h2 className="font-display text-2xl sm:text-3xl font-medium text-slate-900 tracking-tight">
      {children}
    </h2>
  </div>
);

const StatBox = ({ label, value, sub, tone = "default" }) => (
  <div className="border border-slate-200 bg-white rounded-sm p-5">
    <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">
      {label}
    </div>
    <div
      className={`mt-2 font-display text-2xl sm:text-3xl font-light tabular-nums ${
        tone === "positive" ? "text-emerald-700" : "text-[#1E3A6F]"
      }`}
    >
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-slate-500 font-light">{sub}</div>}
  </div>
);

const Row = ({ label, value, meta }) => (
  <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-slate-100 last:border-b-0">
    <span className="text-sm text-slate-700 font-light">{label}</span>
    <span className="flex items-baseline gap-3">
      {meta && <span className="text-xs text-slate-500">{meta}</span>}
      <span className="font-display text-sm text-slate-900 tabular-nums whitespace-nowrap">
        {value}
      </span>
    </span>
  </div>
);

const Bar = ({ label, pct, value, tone = "navy" }) => (
  <div className="flex items-center gap-3">
    <span className="w-36 text-xs text-slate-600 font-light truncate">{label}</span>
    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{
          width: `${pct}%`,
          background:
            tone === "emerald"
              ? "linear-gradient(90deg, #047857 0%, #10B981 100%)"
              : "linear-gradient(90deg, #1E3A6F 0%, #2C4A8C 100%)",
        }}
      />
    </div>
    <span className="w-24 text-right text-xs font-display text-slate-900 tabular-nums">
      {value}
    </span>
  </div>
);

const Finding = ({ icon: Icon, tone, title, body, impact }) => {
  const toneMap = {
    warn: "text-amber-700 bg-amber-50 border-amber-200",
    opp: "text-[#1E3A6F] bg-slate-50 border-slate-200",
    good: "text-emerald-700 bg-emerald-50 border-emerald-200",
  };
  return (
    <div className="border border-slate-200 bg-white rounded-sm p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className={`flex-none w-10 h-10 rounded-sm border flex items-center justify-center ${toneMap[tone]}`}>
          <Icon size={18} weight="duotone" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-medium text-slate-900">{title}</h3>
          <p className="mt-2 text-sm text-slate-600 font-light leading-relaxed">{body}</p>
          {impact && (
            <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-mark uppercase tracking-[0.18em] text-emerald-700">
              <ChartLineUp size={12} weight="bold" /> Est. impact: {impact}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ------- Page -------
export default function SampleReview() {
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    document.title =
      "Sample Payments Review — Flow Control Framework™ | Cody Payments Consulting";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "An illustrative sample of a Flow Control Framework™ Payments Review — a structured, pillar-by-pillar diagnosis of pay-ins, payouts, and partner architecture for a mid-market operator."
      );
    }
  }, []);

  return (
    <div className="bg-[#F6F7F5] text-slate-900 min-h-screen font-body" data-testid="sample-review-page">
      {/* Document header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft size={16} weight="bold" />
            Back
          </Link>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Cody Payments Consulting" className="h-12 w-12 object-contain" />
            <span className="hidden sm:inline-flex items-center font-mark text-[10px] uppercase tracking-[0.3em] text-slate-500 border-l border-slate-300 pl-3">
              Sample Deliverable
            </span>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="print-sample"
            className="hidden sm:inline-flex items-center gap-2 text-xs font-mark uppercase tracking-[0.22em] text-slate-600 hover:text-slate-900 transition-colors"
          >
            <Printer size={14} weight="duotone" /> Print
          </button>
        </div>
      </header>

      {/* Document body */}
      <main className="max-w-5xl mx-auto px-5 sm:px-10 py-12 sm:py-20">
        {/* Cover */}
        <section className="relative border border-slate-200 bg-white rounded-sm p-6 sm:p-12 overflow-hidden">
          <div
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40 blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(30,58,111,0.35) 0%, transparent 65%)" }}
          />
          <div className="relative">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 font-mark text-[10px] uppercase tracking-[0.28em] text-[#1E3A6F]">
                <span className="block w-6 h-px bg-[#1E3A6F]" />
                Framework Review · Sample
              </span>
              <span className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Illustrative · Anonymized
              </span>
            </div>

            <h1 className="mt-6 font-display text-[32px] leading-[1.05] sm:text-5xl font-light tracking-tight text-slate-900">
              Flow Control Framework™ Review for a
              <br />
              <span className="font-mark font-semibold text-[#1E3A6F]">Mid-Market eCommerce Operator</span>
            </h1>

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatBox label="Annual Volume" value="$240M" sub="Processed across all rails" />
              <StatBox label="Avg Ticket" value="$87" sub="~2.76M transactions / yr" />
              <StatBox label="Current Effective" value="2.72%" sub="Blended, plus $0.23 / txn" />
              <StatBox
                label="Target Effective"
                value="2.21%"
                sub="Plus $0.17 / txn · post-optimization"
                tone="positive"
              />
            </div>

            <div className="mt-10 grid sm:grid-cols-[1fr_auto] gap-6 items-end border-t border-slate-200 pt-6">
              <div>
                <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  Executive Summary
                </div>
                <ul className="mt-3 space-y-2 text-sm text-slate-700 font-light leading-relaxed">
                  <li className="flex gap-3">
                    <span className="font-display text-[#1E3A6F] tabular-nums">01</span>
                    Interchange is not the issue — processor markup and non-qualified downgrades account for
                    <span className="text-slate-900 font-normal"> 61%</span> of the addressable spend.
                  </li>
                  <li className="flex gap-3">
                    <span className="font-display text-[#1E3A6F] tabular-nums">02</span>
                    Authorization rate can move from <span className="text-slate-900 font-normal">91.4% → 94.3%</span>
                    &nbsp;by restructuring retries, tokenization, and BIN-level routing.
                  </li>
                  <li className="flex gap-3">
                    <span className="font-display text-[#1E3A6F] tabular-nums">03</span>
                    Payouts over-rely on Wire and same-day ACH; <span className="text-slate-900 font-normal">RTP / FedNow</span> would remove ~$420K in annual rail cost.
                  </li>
                  <li className="flex gap-3">
                    <span className="font-display text-[#1E3A6F] tabular-nums">04</span>
                    PCI DSS scope is SAQ D–level today; a tokenized architecture moves it to SAQ A
                    and materially reduces audit burden.
                  </li>
                </ul>
              </div>
              <div className="bg-slate-900 text-white rounded-sm p-5 w-full sm:w-64">
                <div className="font-mark text-[9px] uppercase tracking-[0.24em] text-slate-400">
                  Estimated Annualized Impact
                </div>
                <div className="mt-2 font-display text-3xl font-light text-white tabular-nums">
                  $1.22M – $1.48M
                </div>
                <div className="mt-1 text-xs text-slate-400 font-light">
                  Cost reduction + payout rail mix + authorization lift
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mark uppercase tracking-[0.22em] text-slate-500">
              <span>Prepared by Cody Payments Consulting</span>
              <span>{today}</span>
              <span>Confidential · Illustrative Sample</span>
            </div>
          </div>
        </section>

        {/* 01 - Flow Design */}
        <section className="mt-16" data-testid="sample-section-flow-design">
          <SectionLabel num="01">Flow Design</SectionLabel>
          <p className="max-w-3xl text-sm sm:text-base text-slate-600 font-light leading-relaxed mb-8">
            How money should move through the business — pay-ins, payouts, cards, and stored value mapped
            end-to-end from initiation to settlement. Baseline captured before redesign.
          </p>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="border border-slate-200 bg-white rounded-sm p-6">
              <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
                Volume mix by modality
              </div>
              <div className="space-y-3">
                <Bar label="Card Not Present (eCom)" pct={78} value="$187.2M · 78%" />
                <Bar label="Card Present" pct={12} value="$28.8M · 12%" />
                <Bar label="ACH (pay-ins)" pct={8} value="$19.2M · 8%" />
                <Bar label="Wire (B2B inbound)" pct={2} value="$4.8M · 2%" />
              </div>
            </div>

            <div className="border border-slate-200 bg-white rounded-sm p-6">
              <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
                Effective cost by modality
              </div>
              <div className="space-y-0">
                <Row label="Card Not Present" value="2.89% + $0.27" meta="post-downgrades" />
                <Row label="Card Present" value="2.34% + $0.18" meta="debit-heavy" />
                <Row label="ACH" value="$1.42 / txn" meta="flat" />
                <Row label="Wire (inbound)" value="$22.00 / txn" meta="bank markup" />
                <Row label="Blended" value="2.72% + $0.23" meta="across volume" />
              </div>
            </div>

            <div className="border border-slate-200 bg-white rounded-sm p-6">
              <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
                Authorization performance
              </div>
              <div className="space-y-0">
                <Row label="Overall auth rate" value="91.4%" meta="trailing 90d" />
                <Row label="US-issued cards" value="93.6%" />
                <Row label="Non-US issuers" value="82.1%" meta="opportunity" />
                <Row label="First-retry recovery" value="41%" meta="vs. 62% benchmark" />
                <Row label="3DS frictionless rate" value="67%" />
                <Row label="Token provisioning" value="38%" meta="underutilized" />
              </div>
            </div>

            <div className="border border-slate-200 bg-white rounded-sm p-6">
              <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
                Payouts profile
              </div>
              <div className="space-y-0">
                <Row label="Vendor / supplier payouts" value="Wire · same-day ACH" />
                <Row label="Refunds" value="Original card · 3-5d" />
                <Row label="Marketplace disbursements" value="ACH · next-day" />
                <Row label="Avg refund cost" value="$0.00 + slow UX" />
                <Row label="RTP / FedNow usage" value="0%" meta="not yet enabled" />
              </div>
            </div>
          </div>
        </section>

        {/* 02 - Control Layer */}
        <section className="mt-16" data-testid="sample-section-control-layer">
          <SectionLabel num="02">Control Layer</SectionLabel>
          <p className="max-w-3xl text-sm sm:text-base text-slate-600 font-light leading-relaxed mb-8">
            Ownership, approval logic, and accountability across the stack. Today, the pay-ins environment
            is functional but under-negotiated and under-controlled — three themes dominate the diagnosis.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <Finding
              icon={Warning}
              tone="warn"
              title="Non-qualified downgrades — 0.31% preventable"
              body="Approximately 18% of CNP credit volume is settling at Standard / EIRF interchange tiers due to missing Level 2 data, late settlement, and AVS mismatches. Enforcing Level 2/3 capture and 24h settlement recovers most of it."
              impact="~$575K / yr"
            />
            <Finding
              icon={CurrencyDollar}
              tone="opp"
              title="Processor markup — 0.14% renegotiation room"
              body="Current IC+ markup of 0.24% + $0.12 is 6–8 bps above market for this volume band. Benchmarked against three comparable processors. No technical migration required — same-counterparty renegotiation."
              impact="~$336K / yr"
            />
            <Finding
              icon={Gauge}
              tone="opp"
              title="Authorization — 2.9 pt lift available"
              body="BIN-level routing, network token provisioning, and a structured retry ladder (not same-processor bounce) moves overall auth from 91.4% to 94.3%. Non-US issuer auth is the largest single gap."
              impact="~$428K revenue recovery"
            />
            <Finding
              icon={Shield}
              tone="opp"
              title="PCI DSS — SAQ D → SAQ A path"
              body="PAN touches merchant systems in two places (legacy subscription portal, CSR tool). Moving both to a hosted tokenization flow reduces scope to SAQ A and cuts audit effort by an estimated 60%."
              impact="Audit & risk reduction"
            />
            <Finding
              icon={CreditCard}
              tone="warn"
              title="3DS strategy — over-challenging US issuers"
              body="3DS challenge rate for US-issued Visa is 11.2%; most should be frictionless under EMV 3DS 2.2. Rules are over-broad and treat low-risk returning customers identically to new accounts."
              impact="Conversion + fraud balance"
            />
            <Finding
              icon={ArrowsLeftRight}
              tone="opp"
              title="Debit routing — Durbin regulated volume unrouted"
              body="Durbin-regulated debit (~$9.4M annually) currently defaults to signature-debit rails. Enabling least-cost debit routing on eligible issuers saves an additional 0.08% on that slice."
              impact="~$75K / yr"
            />
          </div>
        </section>

        {/* 03 - Partner Architecture */}
        <section className="mt-16" data-testid="sample-section-partner-architecture">
          <SectionLabel num="03">Partner Architecture</SectionLabel>
          <p className="max-w-3xl text-sm sm:text-base text-slate-600 font-light leading-relaxed mb-8">
            Processors, banks, networks, and orchestration layers — aligned against the flow design. Payouts
            over-rely on Wire and traditional same-day ACH; a rail-mix redesign and consolidated disbursement
            layer addresses cost, speed, and reconciliation together.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <Finding
              icon={Bank}
              tone="opp"
              title="Supplier payouts — migrate to RTP / FedNow"
              body="72% of outbound supplier Wire volume is domestic, sub-$50K, and delivery-time-sensitive. RTP ($0.12 effective) or FedNow replaces those at ~1% of Wire cost with real-time settlement."
              impact="~$320K / yr"
            />
            <Finding
              icon={CurrencyDollar}
              tone="opp"
              title="Refund mechanics — push to card instead of original-card reversal"
              body="Push-to-debit refunds settle in minutes versus 3–5 days. Implemented selectively for high-value customers, it reduces refund-related CS volume by an estimated 35%."
              impact="CX + retention"
            />
            <Finding
              icon={Coins}
              tone="opp"
              title="International supplier payouts — stablecoin rail"
              body="Cross-border supplier payouts (~$8M/yr) currently flow via international Wire with ~4.2% all-in cost once FX spread and receiving-bank fees are counted. A stablecoin rail reduces that to ~0.35% with same-hour settlement."
              impact="~$310K / yr + speed"
            />
            <Finding
              icon={Gauge}
              tone="warn"
              title="Reconciliation — three disconnected systems"
              body="Disbursements originate from ERP, the subscription platform, and a CS tool. Each writes to a different account. A unified ledger / outbound orchestration layer removes manual daily reconciliation."
              impact="~0.5 FTE recovered"
            />
          </div>
        </section>

        {/* 04 - Economic Engine */}
        <section className="mt-16" data-testid="sample-section-economic-engine">
          <SectionLabel num="04">Economic Engine</SectionLabel>
          <p className="max-w-3xl text-sm sm:text-base text-slate-600 font-light leading-relaxed mb-8">
            Where money is made or lost across interchange, processor markup, rail mix, and partner incentives.
            Prioritized by impact and effort so structural economics — not one-off renegotiation — drive results.
          </p>

          <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 px-5 py-3 border-b border-slate-200 bg-slate-50 font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">
              <span>#</span>
              <span>Recommendation</span>
              <span className="text-right">Impact</span>
              <span className="text-right">Effort</span>
            </div>
            {[
              { n: "01", r: "Enforce Level 2/3 data capture on CNP credit", i: "$575K", e: "Low" },
              { n: "02", r: "Renegotiate IC+ markup with current processor (benchmarked)", i: "$336K", e: "Low" },
              { n: "03", r: "Restructure retry ladder across processors, not same-processor", i: "$280K", e: "Med" },
              { n: "04", r: "Provision network tokens at checkout and for stored credentials", i: "$148K", e: "Med" },
              { n: "05", r: "Shift domestic supplier Wire volume to RTP / FedNow", i: "$320K", e: "Med" },
              { n: "06", r: "Pilot stablecoin rail for international supplier payouts", i: "$310K", e: "High" },
              { n: "07", r: "Move subscription portal + CSR tool to hosted tokenization (SAQ A)", i: "Audit relief", e: "Med" },
              { n: "08", r: "Rebuild 3DS rules — exempt low-risk returning customers", i: "Conversion lift", e: "Low" },
              { n: "09", r: "Enable least-cost debit routing on regulated Durbin volume", i: "$75K", e: "Low" },
              { n: "10", r: "Consolidate outbound disbursements into a single orchestration layer", i: "0.5 FTE", e: "High" },
            ].map((row) => (
              <div
                key={row.n}
                className="grid grid-cols-[auto_1fr_auto_auto] gap-4 px-5 py-3.5 border-b border-slate-100 last:border-b-0 items-baseline"
              >
                <span className="font-display text-sm text-[#1E3A6F] tabular-nums">{row.n}</span>
                <span className="text-sm text-slate-700 font-light">{row.r}</span>
                <span className="text-right text-sm font-display text-slate-900 tabular-nums whitespace-nowrap">
                  {row.i}
                </span>
                <span
                  className={`text-right text-[11px] font-mark uppercase tracking-[0.18em] ${
                    row.e === "Low"
                      ? "text-emerald-700"
                      : row.e === "Med"
                      ? "text-amber-700"
                      : "text-slate-500"
                  }`}
                >
                  {row.e}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* 05 - Execution Integrity */}
        <section className="mt-16" data-testid="sample-section-execution-integrity">
          <SectionLabel num="05">Execution Integrity</SectionLabel>
          <p className="max-w-3xl text-sm sm:text-base text-slate-600 font-light leading-relaxed mb-8">
            Reliability at scale: authorization, reconciliation, payout accuracy, and operational consistency.
            Quantified impact, payback period, and risk notes tied to the recommendations above.
          </p>

          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
            <div className="border border-slate-200 bg-white rounded-sm p-6">
              <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
                Savings contribution
              </div>
              <div className="space-y-3">
                <Bar label="Interchange downgrades recovered" pct={39} value="$575K" />
                <Bar label="Processor markup renegotiation" pct={23} value="$336K" />
                <Bar label="Payouts rail-mix (RTP + stablecoin)" pct={21} value="$630K" />
                <Bar label="Authorization lift" pct={10} value="$148K" />
                <Bar label="Debit routing + misc." pct={7} value="$101K" />
              </div>
              <div className="mt-6 pt-5 border-t border-slate-200 flex items-baseline justify-between">
                <span className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  Annualized range
                </span>
                <span className="font-display text-2xl font-light text-emerald-700 tabular-nums">
                  $1.22M – $1.48M
                </span>
              </div>
            </div>

            <div className="border border-slate-200 bg-white rounded-sm p-6">
              <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
                Payback
              </div>
              <div className="space-y-0">
                <Row label="Engagement fee (illustrative)" value="$95K" />
                <Row label="Implementation support" value="$60K" meta="optional" />
                <Row label="Total program cost" value="$155K" />
                <Row label="Payback period" value="~6 weeks" meta="on annualized savings" />
              </div>
              <div className="mt-6 pt-5 border-t border-slate-200">
                <div className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-3">
                  Risk notes
                </div>
                <ul className="text-xs text-slate-600 font-light space-y-2 leading-relaxed">
                  <li className="flex gap-2">
                    <span className="text-slate-400">—</span>
                    Processor renegotiation requires counterparty goodwill; fallback is diversification.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-slate-400">—</span>
                    Stablecoin rail requires treasury, AML, and accounting sign-off before pilot.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-slate-400">—</span>
                    PCI scope reduction assumes vendor support for hosted tokenization flows.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 06 - Scale Readiness */}
        <section className="mt-16" data-testid="sample-section-scale-readiness">
          <SectionLabel num="06">Scale Readiness</SectionLabel>
          <p className="max-w-3xl text-sm sm:text-base text-slate-600 font-light leading-relaxed mb-8">
            Sequencing the system for growth — quick wins, structural work, and strategic moves staged over
            90 days so volume, optionality, and durability improve in lockstep.
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                tag: "Days 0 – 30",
                title: "Quick wins",
                items: [
                  "Level 2/3 data capture on CNP credit",
                  "Restructure 3DS rules — exempt low-risk returning customers",
                  "Enable least-cost debit routing on regulated issuers",
                  "Open processor renegotiation with benchmarked IC+ terms",
                ],
              },
              {
                tag: "Days 31 – 60",
                title: "Structural work",
                items: [
                  "Cross-processor retry ladder live for declines",
                  "Network tokenization at checkout + stored credentials",
                  "RTP / FedNow enabled for domestic supplier payouts",
                  "Hosted tokenization flow for subscription portal (SAQ A)",
                ],
              },
              {
                tag: "Days 61 – 90",
                title: "Strategic moves",
                items: [
                  "Stablecoin pilot for international supplier payouts",
                  "Unified outbound disbursement orchestration layer",
                  "Finalize processor agreement at revised terms",
                  "Reporting + reconciliation consolidation",
                ],
              },
            ].map((block) => (
              <div key={block.tag} className="border border-slate-200 bg-white rounded-sm p-5">
                <div className="font-mark text-[10px] uppercase tracking-[0.24em] text-[#1E3A6F]">
                  {block.tag}
                </div>
                <div className="mt-2 font-display text-lg font-medium text-slate-900">
                  {block.title}
                </div>
                <ul className="mt-4 space-y-2.5">
                  {block.items.map((i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600 font-light leading-snug">
                      <CheckCircle size={14} weight="duotone" className="text-[#1E3A6F] mt-0.5 flex-none" />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="mt-20" data-testid="sample-section-cta">
          <div className="relative overflow-hidden rounded-sm border border-slate-900 bg-gradient-to-br from-slate-900 to-[#0A0D14] p-8 sm:p-14">
            <div
              className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-30 blur-3xl"
              style={{ background: "radial-gradient(circle, #1E3A6F 0%, transparent 60%)" }}
            />
            <div className="relative max-w-2xl">
              <span className="inline-flex items-center gap-2 font-mark text-[10px] uppercase tracking-[0.28em] text-slate-300">
                <span className="block w-6 h-px bg-[#C7D2DE]" />
                Your review
              </span>
              <h2 className="mt-4 font-display text-2xl sm:text-4xl font-light tracking-tight text-white leading-tight">
                A real review applies the framework to your
                <span className="font-mark font-semibold text-[#C7D2DE]"> volume, mix, and architecture</span>.
              </h2>
              <p className="mt-5 text-sm sm:text-base text-slate-400 font-light leading-relaxed">
                This sample is illustrative. Engagements produce specifics: line-item processor agreements,
                BIN-level auth data, rail-by-rail payout economics, and a prioritized roadmap — all structured
                through the six pillars of the Flow Control Framework™.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                <Link
                  to="/#contact"
                  data-testid="sample-cta-request"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 transition-all duration-200 px-7 py-3.5 rounded-sm text-sm font-medium"
                >
                  Get a Flow Control Review
                  <ArrowRight size={16} weight="bold" />
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors px-3"
                >
                  <ArrowLeft size={14} weight="bold" />
                  Back to site
                </Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="mt-14 pt-8 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mark uppercase tracking-[0.22em] text-slate-500">
          <span>© {new Date().getFullYear()} Cody Payments Consulting</span>
          <span>Confidential · Illustrative · Not a commitment or advice</span>
        </footer>
      </main>
    </div>
  );
}
